<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hydratedInboxIds = new Set();

const ensureInboxPhoneCopyFormatLoaded = async inboxId => {
  if (!inboxId) return;
  if (hydratedInboxIds.has(Number(inboxId))) return;

  try {
    await store.dispatch('inboxes/get');
    hydratedInboxIds.add(Number(inboxId));
  } catch (error) {
    // Keep existing fallback behavior if inbox refresh fails.
  }
};

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const CALL_BRIDGE_SOURCE = 'chatwoot-whatsapp-calls';
const CALL_BRIDGE_READY_EVENT = 'CW_CALL_BRIDGE_READY';
const CALL_BRIDGE_RESPONSE_EVENT = 'CW_CALL_BRIDGE_RESPONSE';
const CALL_BRIDGE_REQUEST_TIMEOUT_MS = 45000;

const callBridgeConnected = ref(false);
const callPanelOpen = ref(false);
const callRequestPending = ref(false);
const callBulkInput = ref('');
const callQueue = ref([]);
const activeCallItemId = ref(null);
const callLogs = ref([]);
const pendingBridgeRequests = new Map();

const CALL_TEXT = {
  call: 'Ligar',
  calls: 'Chamadas',
  extensionConnected: 'Extensão conectada',
  extensionDisconnected: 'Extensão desconectada',
  currentContact: 'Contato atual',
  phoneNotDetected: 'Telefone não detectado',
  callNow: 'Ligar agora',
  addToQueue: 'Adicionar à fila',
  bulkPlaceholder: 'Cole números para ligar, um por linha',
  addNumbers: 'Adicionar números',
  startQueue: 'Iniciar fila',
  finishAndNext: 'Finalizar e próxima',
  failed: 'Falhou',
  clearFinished: 'Limpar finalizadas',
  queue: 'Fila',
  emptyQueue: 'Nenhum número na fila.',
  events: 'Eventos',
  emptyEvents: 'Sem eventos ainda.',
  statusSeparator: '-',
};

const TARGET_INBOX_IDS = [1, 2];

const parsePhoneLikeValue = rawValue => {
  const value = String(rawValue || '').trim();
  if (!value) return '';
  if (value.endsWith('@g.us')) return '';
  if (value.endsWith('@s.whatsapp.net')) {
    return value.replace('@s.whatsapp.net', '');
  }

  const digitsOnly = value.replace(/\D/g, '');
  if (digitsOnly.length >= 8 && digitsOnly.length <= 15) {
    return value.startsWith('+') ? value : `+${digitsOnly}`;
  }

  return '';
};

const contactPhoneNumber = computed(() => {
  return (
    parsePhoneLikeValue(currentContact.value?.phone_number) ||
    parsePhoneLikeValue(props.chat?.meta?.sender?.phone_number) ||
    parsePhoneLikeValue(currentContact.value?.identifier) ||
    parsePhoneLikeValue(props.chat?.meta?.sender?.identifier) ||
    ''
  );
});

const normalizePhoneForCall = rawValue => {
  let digits = String(rawValue || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.length === 10 || digits.length === 11) digits = `55${digits}`;
  if (digits.length < 11 || digits.length > 13) return '';
  return digits;
};

const formatCallPhone = rawValue => {
  const digits = normalizePhoneForCall(rawValue);
  if (!digits) return '';
  if (digits.startsWith('55') && digits.length >= 12) {
    return `+55 ${digits.slice(2, 4)} ${digits.slice(4)}`;
  }
  return `+${digits}`;
};

const currentCallPhone = computed(() =>
  normalizePhoneForCall(contactPhoneNumber.value)
);

const canStartCurrentCall = computed(
  () =>
    !!currentCallPhone.value &&
    callBridgeConnected.value &&
    !callRequestPending.value
);

const addCallLog = message => {
  callLogs.value = [
    {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      at: new Date().toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
      message,
    },
    ...callLogs.value,
  ].slice(0, 12);
};

const makeCallQueueItem = (phone, source = 'manual') => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  phone,
  source,
  status: 'queued',
  result: '',
});

const queueStatusLabel = status =>
  ({
    queued: 'Na fila',
    calling: 'Chamando',
    done: 'Finalizada',
    failed: 'Falhou',
  })[status] || status;

const extractPhonesForCall = text => {
  const matches =
    String(text || '').match(
      /(?:\+?55\s*)?(?:\(?\d{2}\)?\s*)?(?:9\s*)?\d{4,5}[-.\s]?\d{4}/g
    ) || [];
  return [...new Set(matches.map(normalizePhoneForCall).filter(Boolean))];
};

const sendCallBridgeRequest = phone =>
  new Promise((resolve, reject) => {
    if (!callBridgeConnected.value) {
      reject(new Error('Extensão de chamadas não conectada.'));
      return;
    }

    const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const timeout = window.setTimeout(() => {
      pendingBridgeRequests.delete(requestId);
      reject(new Error('Timeout aguardando resposta da extensão.'));
    }, CALL_BRIDGE_REQUEST_TIMEOUT_MS);

    pendingBridgeRequests.set(requestId, {
      resolve: payload => {
        window.clearTimeout(timeout);
        resolve(payload);
      },
      reject: error => {
        window.clearTimeout(timeout);
        reject(error);
      },
    });

    window.postMessage(
      {
        source: CALL_BRIDGE_SOURCE,
        type: 'CW_CALL_START',
        requestId,
        payload: { phone },
      },
      window.location.origin
    );
  });

const onCallBridgeMessage = event => {
  if (event.source !== window) return;
  if (event.data?.source !== CALL_BRIDGE_SOURCE) return;

  if (event.data.type === CALL_BRIDGE_READY_EVENT) {
    callBridgeConnected.value = !!event.data.payload?.connected;
    return;
  }

  if (event.data.type === CALL_BRIDGE_RESPONSE_EVENT) {
    const pending = pendingBridgeRequests.get(event.data.requestId);
    if (!pending) return;
    pendingBridgeRequests.delete(event.data.requestId);
    pending.resolve(event.data.payload);
  }
};

const startCallToPhone = async phone => {
  const normalized = normalizePhoneForCall(phone);
  if (!normalized) {
    useAlert('Telefone inválido para chamada.');
    return { ok: false, error: 'Telefone inválido.' };
  }

  callRequestPending.value = true;
  addCallLog(`Solicitando chamada para ${formatCallPhone(normalized)}.`);
  try {
    const response = await sendCallBridgeRequest(normalized);
    if (!response?.ok) {
      const message = response?.error || 'A extensão não confirmou a chamada.';
      addCallLog(`Falha em ${formatCallPhone(normalized)}: ${message}`);
      useAlert(message);
      return { ok: false, error: message };
    }

    addCallLog(`WhatsApp Web acionado para ${formatCallPhone(normalized)}.`);
    useAlert('Chamada enviada para o WhatsApp Web.');
    return response;
  } catch (error) {
    addCallLog(`Falha em ${formatCallPhone(normalized)}: ${error.message}`);
    useAlert(error.message);
    return { ok: false, error: error.message };
  } finally {
    callRequestPending.value = false;
  }
};

const onStartCurrentCall = event => {
  event?.preventDefault?.();
  event?.stopPropagation?.();
  startCallToPhone(currentCallPhone.value);
};

const addPhoneToQueue = (phone, source = 'manual') => {
  const normalized = normalizePhoneForCall(phone);
  if (!normalized) return false;
  const alreadyQueued = callQueue.value.some(
    item =>
      item.phone === normalized && ['queued', 'calling'].includes(item.status)
  );
  if (alreadyQueued) return false;
  callQueue.value = [...callQueue.value, makeCallQueueItem(normalized, source)];
  return true;
};

const onAddCurrentToQueue = () => {
  if (addPhoneToQueue(currentCallPhone.value, 'conversation')) {
    callPanelOpen.value = true;
    addCallLog(
      `Contato atual adicionado à fila: ${formatCallPhone(currentCallPhone.value)}.`
    );
  }
};

const onAddBulkPhones = () => {
  const phones = extractPhonesForCall(callBulkInput.value);
  let added = 0;
  phones.forEach(phone => {
    if (addPhoneToQueue(phone, 'bulk')) added += 1;
  });
  callBulkInput.value = '';
  addCallLog(`${added} número(s) adicionados à fila.`);
};

const startNextQueuedCall = async () => {
  if (activeCallItemId.value) {
    useAlert('Finalize a chamada ativa antes de iniciar a próxima.');
    return;
  }

  const item = callQueue.value.find(entry => entry.status === 'queued');
  if (!item) {
    addCallLog('Fila de chamadas concluída.');
    return;
  }

  item.status = 'calling';
  activeCallItemId.value = item.id;
  const response = await startCallToPhone(item.phone);
  if (!response?.ok) {
    item.status = 'failed';
    item.result = response?.error || 'Falhou';
    activeCallItemId.value = null;
  } else {
    item.result = 'Chamada iniciada no WhatsApp Web';
  }
};

const finishActiveCallAndNext = () => {
  const item = callQueue.value.find(
    entry => entry.id === activeCallItemId.value
  );
  if (item) {
    item.status = 'done';
    item.result = 'Finalizada manualmente';
  }
  activeCallItemId.value = null;
  startNextQueuedCall();
};

const failActiveCallAndNext = () => {
  const item = callQueue.value.find(
    entry => entry.id === activeCallItemId.value
  );
  if (item) {
    item.status = 'failed';
    item.result = 'Marcada como falha';
  }
  activeCallItemId.value = null;
  startNextQueuedCall();
};

const removeCallQueueItem = id => {
  callQueue.value = callQueue.value.filter(item => item.id !== id);
  if (activeCallItemId.value === id) activeCallItemId.value = null;
};

const clearFinishedCalls = () => {
  callQueue.value = callQueue.value.filter(item =>
    ['queued', 'calling'].includes(item.status)
  );
};

const isInternalAgendaValue = value =>
  value === true || value === 'true' || value === 1 || value === '1';

const isInternalAgendaContact = computed(
  () =>
    isInternalAgendaValue(
      currentContact.value?.custom_attributes?.internal_agenda
    ) ||
    isInternalAgendaValue(
      props.chat?.meta?.sender?.custom_attributes?.internal_agenda
    )
);

const shouldShowPhoneAsName = computed(() => {
  const inboxId = Number(props.chat?.inbox_id);
  return (
    TARGET_INBOX_IDS.includes(inboxId) &&
    !isInternalAgendaContact.value &&
    !!contactPhoneNumber.value
  );
});

const contactDisplayName = computed(() => {
  if (shouldShowPhoneAsName.value) return contactPhoneNumber.value;
  return (
    currentContact.value?.name ||
    props.chat?.meta?.sender?.name ||
    contactPhoneNumber.value ||
    ''
  );
});

const DEFAULT_PHONE_COPY_FORMAT = null;
const PHONE_COPY_DEBUG_WEBHOOK_URL =
  'https://webhookn8n.projectsuntil2027.shop/webhook/chatwoot-phone-copy-debug-20260315';

const normalizeBrazilPhoneDigits = rawValue => {
  const digits = String(rawValue || '').replace(/\D/g, '');
  if (!digits) return '';

  let nationalDigits = digits;
  if (nationalDigits.startsWith('55') && nationalDigits.length >= 12) {
    nationalDigits = nationalDigits.slice(2);
  }
  if (nationalDigits.length > 11) {
    nationalDigits = nationalDigits.slice(-11);
  }
  if (nationalDigits.length < 10) {
    return '';
  }

  return nationalDigits;
};

const formatPhoneForClipboard = rawValue => {
  const nationalDigits = normalizeBrazilPhoneDigits(rawValue);
  if (!nationalDigits) {
    return String(rawValue || '').trim();
  }

  const ddd = nationalDigits.slice(0, 2);
  const subscriberWithNine =
    nationalDigits.length === 11
      ? nationalDigits.slice(2)
      : `9${nationalDigits.slice(2)}`;
  const subscriberWithoutNine = subscriberWithNine.slice(-8);
  const copyFormat =
    inbox.value?.phone_copy_format || DEFAULT_PHONE_COPY_FORMAT;

  if (!copyFormat) {
    return '';
  }

  switch (copyFormat) {
    case 'br_spaced_55_with_nine':
      return `55 (${ddd}) ${subscriberWithNine.slice(0, 1)} ${subscriberWithNine.slice(1, 5)}-${subscriberWithNine.slice(5)}`;
    case 'digits_55':
      return `55${ddd}${subscriberWithNine}`;
    case 'digits_plus55':
      return `+55${ddd}${subscriberWithNine}`;
    case 'digits_local_with_nine':
      return `${ddd}${subscriberWithNine}`;
    case 'br_spaced_local_with_nine':
      return `(${ddd}) ${subscriberWithNine.slice(0, 1)} ${subscriberWithNine.slice(1, 5)}-${subscriberWithNine.slice(5)}`;
    case 'digits_55_without_nine':
      return `55${ddd}${subscriberWithoutNine}`;
    case 'digits_plus55_without_nine':
      return `+55${ddd}${subscriberWithoutNine}`;
    case 'br_spaced_plus55_with_nine':
      return `+55 (${ddd}) ${subscriberWithNine.slice(0, 1)} ${subscriberWithNine.slice(1, 5)}-${subscriberWithNine.slice(5)}`;
    case 'br_spaced_plus55_without_nine':
      return `+55 (${ddd}) ${subscriberWithoutNine.slice(0, 4)}-${subscriberWithoutNine.slice(4)}`;
    case 'br_spaced_55_without_nine':
      return `55 (${ddd}) ${subscriberWithoutNine.slice(0, 4)}-${subscriberWithoutNine.slice(4)}`;
    default:
      return `+55${ddd}${subscriberWithNine}`;
  }
};

const emitPhoneCopyDebug = payload => {
  try {
    const body = JSON.stringify(payload);
    if (navigator?.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(PHONE_COPY_DEBUG_WEBHOOK_URL, blob);
      return;
    }

    fetch(PHONE_COPY_DEBUG_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (error) {
    // Ignore debug transport failures.
  }
};

const fallbackCopyText = text => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  textarea.style.left = '-9999px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  let copied = false;
  try {
    copied = document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }

  return copied;
};

const writePhoneToClipboard = async text => {
  let readBackText = null;
  let method = 'navigator.clipboard';

  try {
    await navigator.clipboard.writeText(text);
    if (navigator.clipboard?.readText) {
      readBackText = await navigator.clipboard.readText();
      if (readBackText === text) {
        return { method, readBackText };
      }
    } else {
      return { method, readBackText };
    }
  } catch (error) {
    // Fall through to DOM copy fallback.
  }

  const copied = fallbackCopyText(text);
  if (!copied) {
    throw new Error('Clipboard write failed');
  }

  method = 'execCommand';
  if (navigator.clipboard?.readText) {
    try {
      readBackText = await navigator.clipboard.readText();
    } catch (error) {
      readBackText = null;
    }
  }

  return { method, readBackText };
};

const onCopyContactPhone = async event => {
  event?.preventDefault?.();
  event?.stopPropagation?.();

  const rawPhone = contactPhoneNumber.value;
  if (!rawPhone) {
    emitPhoneCopyDebug({
      source: 'conversation_header_copy_phone',
      stage: 'missing_phone',
      conversationId: props.chat?.id || null,
      inboxId: props.chat?.inbox_id || null,
      inboxName: inbox.value?.name || null,
      phoneCopyFormat: inbox.value?.phone_copy_format || null,
      contactPhoneRaw: null,
      formattedPhone: null,
      buttonDisabled: true,
      chatwootUrl: window.location.href,
      ts: new Date().toISOString(),
    });
    return;
  }
  await ensureInboxPhoneCopyFormatLoaded(props.chat?.inbox_id);
  const formattedPhone = formatPhoneForClipboard(rawPhone);
  emitPhoneCopyDebug({
    source: 'conversation_header_copy_phone',
    stage: formattedPhone ? 'before_copy' : 'blocked_no_format',
    conversationId: props.chat?.id || null,
    inboxId: props.chat?.inbox_id || null,
    inboxName: inbox.value?.name || null,
    phoneCopyFormat: inbox.value?.phone_copy_format || null,
    contactPhoneRaw: rawPhone,
    formattedPhone: formattedPhone || null,
    buttonDisabled: !inbox.value?.phone_copy_format,
    chatwootUrl: window.location.href,
    ts: new Date().toISOString(),
  });
  if (!formattedPhone) return;
  const { method, readBackText } = await writePhoneToClipboard(formattedPhone);
  emitPhoneCopyDebug({
    source: 'conversation_header_copy_phone',
    stage: 'copied',
    conversationId: props.chat?.id || null,
    inboxId: props.chat?.inbox_id || null,
    inboxName: inbox.value?.name || null,
    phoneCopyFormat: inbox.value?.phone_copy_format || null,
    contactPhoneRaw: rawPhone,
    formattedPhone,
    clipboardMethod: method,
    readBackText: readBackText || null,
    chatwootUrl: window.location.href,
    ts: new Date().toISOString(),
  });
  useAlert(t('CONTACT_PANEL.COPY_SUCCESSFUL'));
};

watch(
  () => props.chat?.inbox_id,
  inboxId => {
    ensureInboxPhoneCopyFormatLoaded(inboxId);
  },
  { immediate: true }
);

onMounted(() => {
  window.addEventListener('message', onCallBridgeMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener('message', onCallBridgeMessage);
  pendingBridgeRequests.forEach(({ reject }) =>
    reject(new Error('Componente de chamadas desmontado.'))
  );
  pendingBridgeRequests.clear();
});
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 py-2 border-b bg-n-background border-n-weak h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="contactDisplayName"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ contactDisplayName }}
          </span>
          <template v-if="contactPhoneNumber">
            <button
              v-tooltip="$t('CONTACT_PANEL.COPY_SUCCESSFUL')"
              type="button"
              class="inline-flex items-center justify-center p-0.5 text-n-slate-10 hover:text-n-slate-12"
              :disabled="!inbox?.phone_copy_format"
              :class="{
                'opacity-40 cursor-not-allowed hover:text-n-slate-10':
                  !inbox?.phone_copy_format,
              }"
              @click.stop.prevent="onCopyContactPhone"
            >
              <span class="i-lucide-clipboard size-3.5" />
            </button>
          </template>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
        >
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <button
        v-tooltip="
          callBridgeConnected
            ? 'Ligar pelo WhatsApp Web'
            : 'Extensão de chamadas desconectada'
        "
        type="button"
        class="inline-flex items-center justify-center h-8 gap-1 px-2 text-xs font-medium border rounded-md border-n-weak text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40 disabled:cursor-not-allowed"
        :disabled="!canStartCurrentCall"
        @click.stop.prevent="onStartCurrentCall"
      >
        <span class="i-lucide-phone size-3.5" />
        <span>{{ CALL_TEXT.call }}</span>
      </button>
      <button
        v-tooltip="'Fila de chamadas'"
        type="button"
        class="inline-flex items-center justify-center h-8 gap-1 px-2 text-xs font-medium border rounded-md border-n-weak text-n-slate-11 hover:bg-n-slate-2"
        @click.stop.prevent="callPanelOpen = !callPanelOpen"
      >
        <span class="i-lucide-list-video size-3.5" />
        <span>{{ CALL_TEXT.calls }}</span>
      </button>
      <MoreActions :conversation-id="currentChat.id" />
    </div>
    <div
      v-if="callPanelOpen"
      class="fixed right-4 bottom-4 z-[9999] flex max-h-[calc(100vh-2rem)] w-[360px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border border-n-weak bg-n-background shadow-xl"
    >
      <div
        class="flex items-center justify-between gap-3 border-b border-n-weak px-3 py-2"
      >
        <div class="min-w-0">
          <div class="text-sm font-semibold text-n-slate-12">
            {{ CALL_TEXT.calls }}
          </div>
          <div class="text-xs text-n-slate-10">
            {{
              callBridgeConnected
                ? CALL_TEXT.extensionConnected
                : CALL_TEXT.extensionDisconnected
            }}
          </div>
        </div>
        <button
          type="button"
          class="inline-flex size-7 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-slate-2"
          @click="callPanelOpen = false"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>
      <div class="flex-1 space-y-3 overflow-y-auto p-3">
        <div class="rounded-md border border-n-weak p-2">
          <div class="text-xs text-n-slate-10">
            {{ CALL_TEXT.currentContact }}
          </div>
          <div class="text-sm font-medium text-n-slate-12">
            {{
              currentCallPhone
                ? formatCallPhone(currentCallPhone)
                : CALL_TEXT.phoneNotDetected
            }}
          </div>
          <div class="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              class="rounded-md border border-n-weak px-2 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
              :disabled="!canStartCurrentCall"
              @click="onStartCurrentCall"
            >
              {{ CALL_TEXT.callNow }}
            </button>
            <button
              type="button"
              class="rounded-md border border-n-weak px-2 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
              :disabled="!currentCallPhone"
              @click="onAddCurrentToQueue"
            >
              {{ CALL_TEXT.addToQueue }}
            </button>
          </div>
        </div>

        <div>
          <textarea
            v-model="callBulkInput"
            class="h-20 w-full resize-y rounded-md border border-n-weak bg-n-background px-2 py-1 text-sm text-n-slate-12 outline-none"
            :placeholder="CALL_TEXT.bulkPlaceholder"
          />
          <button
            type="button"
            class="mt-2 w-full rounded-md border border-n-weak px-2 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-slate-2"
            @click="onAddBulkPhones"
          >
            {{ CALL_TEXT.addNumbers }}
          </button>
        </div>

        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            class="rounded-md border border-n-weak px-2 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
            :disabled="!callBridgeConnected || callRequestPending"
            @click="startNextQueuedCall"
          >
            {{ CALL_TEXT.startQueue }}
          </button>
          <button
            type="button"
            class="rounded-md border border-n-weak px-2 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
            :disabled="!activeCallItemId"
            @click="finishActiveCallAndNext"
          >
            {{ CALL_TEXT.finishAndNext }}
          </button>
          <button
            type="button"
            class="rounded-md border border-n-weak px-2 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-slate-2 disabled:opacity-40"
            :disabled="!activeCallItemId"
            @click="failActiveCallAndNext"
          >
            {{ CALL_TEXT.failed }}
          </button>
          <button
            type="button"
            class="rounded-md border border-n-weak px-2 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-slate-2"
            @click="clearFinishedCalls"
          >
            {{ CALL_TEXT.clearFinished }}
          </button>
        </div>

        <div>
          <div class="mb-1 text-xs font-semibold uppercase text-n-slate-10">
            {{ CALL_TEXT.queue }}
          </div>
          <div
            v-if="!callQueue.length"
            class="rounded-md border border-dashed border-n-weak p-3 text-center text-xs text-n-slate-10"
          >
            {{ CALL_TEXT.emptyQueue }}
          </div>
          <div
            v-for="item in callQueue"
            :key="item.id"
            class="mb-2 flex items-center justify-between gap-2 rounded-md border border-n-weak p-2"
            :class="{
              'bg-n-blue-3 border-n-blue-7': item.status === 'calling',
              'bg-n-teal-3 border-n-teal-7': item.status === 'done',
              'bg-n-ruby-3 border-n-ruby-7': item.status === 'failed',
            }"
          >
            <div class="min-w-0">
              <div class="text-sm font-medium text-n-slate-12">
                {{ formatCallPhone(item.phone) }}
              </div>
              <div class="text-xs text-n-slate-10">
                {{ queueStatusLabel(item.status) }}
                <span v-if="item.result">
                  {{ CALL_TEXT.statusSeparator }} {{ item.result }}</span
                >
              </div>
            </div>
            <button
              type="button"
              class="inline-flex size-7 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-slate-2"
              @click="removeCallQueueItem(item.id)"
            >
              <span class="i-lucide-x size-3.5" />
            </button>
          </div>
        </div>

        <div>
          <div class="mb-1 text-xs font-semibold uppercase text-n-slate-10">
            {{ CALL_TEXT.events }}
          </div>
          <div
            v-if="!callLogs.length"
            class="rounded-md border border-dashed border-n-weak p-3 text-center text-xs text-n-slate-10"
          >
            {{ CALL_TEXT.emptyEvents }}
          </div>
          <div
            v-for="entry in callLogs"
            :key="entry.id"
            class="border-b border-n-weak py-1 text-xs text-n-slate-11"
          >
            <span class="mr-2 text-n-slate-9">{{ entry.at }}</span>
            {{ entry.message }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
